/**
 * offlineQueue.js
 * Serviço de fila offline para o Climorax.
 * Armazena operações pendentes no localStorage e sincroniza quando
 * a conexão é restabelecida.
 *
 * Tipos suportados:
 *   - 'photo'  → upload de foto para fotos_talhao
 *   - 'event'  → inserção em eventos_talhao
 */

import { supabase } from '../supabaseClient';

const QUEUE_KEY = 'climorax_offline_queue';
export const QUEUE_UPDATED_EVENT = 'climorax_queue_updated';

// ─── helpers ────────────────────────────────────────────────────────────────

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

export function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function setQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    // localStorage cheio — remove itens mais antigos e tenta novamente
    const trimmed = queue.slice(-5);
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
    } catch {
      console.error('[offlineQueue] Não foi possível salvar na fila:', e);
      return;
    }
  }
  window.dispatchEvent(new Event(QUEUE_UPDATED_EVENT));
}

export function getPendingCount() {
  return getQueue().filter(i => i.status === 'pending').length;
}

// ─── adicionar itens ─────────────────────────────────────────────────────────

/**
 * Enfileira uma foto para upload posterior.
 * @param {object} params
 * @param {string} params.talhaoId
 * @param {string} params.userId
 * @param {string} params.observacao
 * @param {string} params.dataFoto   ISO string
 * @param {string} params.photoBase64  sem prefixo data:image/...
 * @param {string} params.photoFormat  'jpeg' | 'png'
 * @returns {object} item enfileirado (com id e status 'pending')
 */
export function addPhotoToQueue({ talhaoId, plantioId = null, userId, observacao, dataFoto, photoBase64, photoFormat }) {
  const item = {
    id: generateId(),
    type: 'photo',
    talhaoId,
    plantioId,
    userId,
    observacao: observacao || '',
    dataFoto: dataFoto || new Date().toISOString(),
    photoBase64,
    photoFormat: photoFormat || 'jpeg',
    createdAt: new Date().toISOString(),
    status: 'pending',
  };
  setQueue([...getQueue(), item]);
  return item;
}

/**
 * Enfileira um evento para inserção posterior.
 * @param {object} eventData  objeto pronto para inserir em eventos_talhao
 * @returns {object} item enfileirado (com id e status 'pending')
 */
export function addEventToQueue(eventData) {
  const item = {
    id: generateId(),
    type: 'event',
    eventData,
    createdAt: new Date().toISOString(),
    status: 'pending',
  };
  setQueue([...getQueue(), item]);
  return item;
}

// ─── remover item da fila (ex: foto offline excluída antes de sincronizar) ───

export function removeFromQueue(itemId) {
  const updated = getQueue().filter(i => i.id !== itemId);
  setQueue(updated);
}

// ─── sincronização ───────────────────────────────────────────────────────────

/**
 * Processa todos os itens pendentes da fila.
 * Retorna { synced, failed } com os totais.
 */
export async function syncQueue() {
  const queue = getQueue();
  const pending = queue.filter(i => i.status === 'pending');
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;
  const updated = [...queue];

  for (const item of pending) {
    const idx = updated.findIndex(i => i.id === item.id);
    try {
      if (item.type === 'photo') {
        // Converte base64 → Blob
        const byteString = atob(item.photoBase64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: `image/${item.photoFormat || 'jpeg'}` });

        const fileName = `foto_${Date.now()}_${item.id}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('fotos-talhao')
          .upload(fileName, blob);
        if (uploadError) throw uploadError;

        const { error: insertError } = await supabase
          .from('fotos_talhao')
          .insert([{
            talhao_id: item.talhaoId,
            plantio_id: item.plantioId ?? null,
            foto_path: fileName,
            observacao: item.observacao,
            data_foto: item.dataFoto,
            user_id: item.userId,
          }]);
        if (insertError) throw insertError;

      } else if (item.type === 'event') {
        const { error } = await supabase
          .from('eventos_talhao')
          .insert([item.eventData]);
        if (error) throw error;
      }

      updated[idx] = { ...updated[idx], status: 'synced' };
      synced++;
    } catch (e) {
      console.error('[offlineQueue] Falha ao sincronizar item', item.id, e);
      updated[idx] = { ...updated[idx], status: 'error' };
      failed++;
    }
  }

  // Remove itens sincronizados; mantém os com erro para próxima tentativa
  setQueue(updated.filter(i => i.status !== 'synced'));
  return { synced, failed };
}
